import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, Typography, Box, Container, Button, Backdrop, CircularProgress } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import { LoadingButton } from '@mui/lab';
import Cookies from 'js-cookie';
import jwtdecode from 'jwt-decode';
import apiClient from '../../services/apiClient';
import loginServices from '../../services/loginServices';
import SnackBar from '../../components/snackbar/snackbar';
import useLoginFlow from '../../container/login/hooks/useLoginFlow';
import ImagePanel from '../../container/login/components/ImagePanel';
import OnBoard from '../../container/login/loginonboard';
import Logo from '../../assets/logo/logo.png';

const OTP_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 30;

export const VerifyOtp = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('translation');
  const { getLoginUserDetails } = useLoginFlow();

  const email = location.state?.email || '';
  const firstName = location.state?.firstName || '';
  const redirectParam = location.state?.redirect || '';

  const [digits, setDigits] = useState(Array(OTP_LENGTH).fill(''));
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_SECONDS);
  const [snackbar, setSnackbar] = useState(false);
  const [snackbarTitle, setSnackbarTitle] = useState('');
  const [showLearningJourney, setShowLearningJourney] = useState(false);
  const [onboardingLoading, setOnboardingLoading] = useState(false);
  const inputRefs = useRef([]);
  useEffect(() => {
    if (!email) {
      navigate('/sign-up', { replace: true });
      return;
    }
    focusInput(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, navigate]);

 

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setInterval(() => setCooldown((c) => c - 1), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);
  
 useEffect(() => {
  if (!showLearningJourney) return undefined;
  window.history.pushState(null, '', '/dashboard');
  const blockBack = () => window.history.pushState(null, '', '/dashboard');
  window.addEventListener('popstate', blockBack);
  return () => window.removeEventListener('popstate', blockBack);
}, [showLearningJourney]);
  
  const focusInput = (index) => {
    if (inputRefs.current[index]) {
      inputRefs.current[index].focus();
    }
  };
  const handleDigitChange = (index, rawValue) => {
    const value = rawValue.replace(/\D/g, '');
    if (!value) {
      setDigits((prev) => {
        const next = [...prev];
        next[index] = '';
        return next;
      });
      return;
    }
    setDigits((prev) => {
      const next = [...prev];
      value.split('').forEach((char, offset) => {
        if (index + offset < OTP_LENGTH) {
          next[index + offset] = char;
        }
      });
      return next;
    });

    const nextIndex = Math.min(index + value.length, OTP_LENGTH - 1);
    focusInput(nextIndex);
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      focusInput(index - 1);
    }
  };

  const otp = digits.join('');

  const handleOnBoardComplete = async () => {
    setOnboardingLoading(true);
    setShowLearningJourney(false);
    if (redirectParam) {
      localStorage.setItem('redirectAfterLogin', redirectParam);
    }
    await getLoginUserDetails();
    setOnboardingLoading(false);
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (otp.length !== OTP_LENGTH) {
      setSnackbarTitle('Please enter the complete 6-digit code.');
      setSnackbar(true);
      return;
    }
    setLoading(true);
    try {
      const response = await loginServices.verifySignupOtp({
        emailAddress: email,
        otp,
      });
      if (response.ok) {
        apiClient.setHeader('Authorization', `BEARER ${response.data.token}`);
        Cookies.set('token', response.data.token);
        Cookies.set('refreshToken', response.data.refreshToken);
        Cookies.set('isLogIn', true);
        const decoded = jwtdecode(response.data.token);
        Cookies.set('tokenExpireTime', decoded.exp);
        setLoading(false);
        
        setShowLearningJourney(true);
        return;
      }
      setSnackbarTitle(response.data?.message || 'Invalid OTP. Please try again.');
      setSnackbar(true);
    } catch (error) {
      setSnackbarTitle('Something went wrong. Please try again.');
      setSnackbar(true);
    }
    setLoading(false);
  };
  const handleResend = async () => {
    if (cooldown > 0) return;
    setResendLoading(true);
    try {
      const response = await loginServices.sendSignupOtp({ email, firstName });
      if (response.ok) {
        setSnackbarTitle('A new OTP has been sent to your email address.');
        setSnackbar(true);
        setDigits(Array(OTP_LENGTH).fill(''));
        focusInput(0);
        setCooldown(RESEND_COOLDOWN_SECONDS);
      } else {
        setSnackbarTitle(response.data?.message || 'Could not resend OTP. Please try again.');
        setSnackbar(true);
      }
    } catch (error) {
      setSnackbarTitle('Something went wrong. Please try again.');
      setSnackbar(true);
    }
    setResendLoading(false);
  };
  return (
    <Box className={classes.mainContainer}>
      <Container
        maxWidth="lg"
        style={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '24px 16px' }}
      >
        <Grid container spacing={2} id="verifyOtpPanel" alignItems="stretch" sx={{ justifyContent: 'center !important' }}>
          {showLearningJourney === false && (
            <Grid item xs={12} md={4.5}>
              <Box className={classes.formcontainer} style={{ height: '100%' }}>
                <Grid container spacing={2} sx={{ display: 'flex', alignContent: 'center', height: '100%' }}>
                  <Grid item xs={12}>
                    <img
                      src={Logo}
                      alt="logo"
                      style={{ display: 'block', margin: 'auto', height: '48px', width: '102px' }}
                    />
                    <Typography
                      color="#000000"
                      align="center"
                      variant="h4"
                      sx={{
                        fontSize: { md: '22px', lg: '23px !important' },
                        marginTop: '20px',
                        marginBottom: '10px !important',
                        lineHeight: '1.1 !important',
                      }}
                    >
                      Open your inbox and verify your email ID
                    </Typography>
                    <ul style={{ marginLeft: '30px' }}>
                      <li style={{ fontSize: '12px', color: '#000', fontWeight: 500, marginTop: '5px' }}>
                        To activate your keySkillset account, enter the 6-digit code we sent to{' '}
                        <b>{email || 'your email'}</b>.
                      </li>
                      <li style={{ fontSize: '12px', color: '#000', fontWeight: 500, marginTop: '5px' }}>
                        If you haven&apos;t received it, please check spam or junk or recycle bin.
                      </li>
                    </ul>
                  </Grid>
                  <Grid item xs={12}>
                    <form onSubmit={handleSubmit}>
                      <Box className={classes.otpRow}>
                        {digits.map((digit, index) => (
                          <input
                            // eslint-disable-next-line react/no-array-index-key
                            key={index}
                            ref={(el) => { inputRefs.current[index] = el; }}
                            className={classes.otpInput}
                            value={digit}
                            onChange={(e) => handleDigitChange(index, e.target.value)}
                            onKeyDown={(e) => handleKeyDown(index, e)}
                            inputMode="numeric"
                            maxLength={OTP_LENGTH}
                          />
                        ))}
                      </Box>
                      <LoadingButton
                        fullWidth
                        size="medium"
                        type="submit"
                        variant="contained"
                        loading={loading}
                        sx={{
                          borderRadius: '30px !important',
                          background: '#00b673',
                          marginTop: '20px',
                          '&:hover': { backgroundColor: '#00b673 !important' },
                        }}
                      >
                        {t('Verify & Continue')}
                      </LoadingButton>
                      <Box sx={{ textAlign: 'center', marginTop: '16px' }}>
                        <Typography variant="body2" sx={{ fontSize: '13px' }}>
                          {t("Didn't receive the code?")} &nbsp;
                          {cooldown > 0 ? (
                            <span style={{ color: '#999' }}>Resend in {cooldown}s</span>
                          ) : (
                            <Button
                              onClick={handleResend}
                              disabled={resendLoading}
                              sx={{ color: '#437BFC', fontSize: '13px', textTransform: 'none', padding: 0, minWidth: 'auto' }}
                            >
                              Resend OTP
                            </Button>
                          )}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center', marginTop: '8px' }}>
                        <Button
                          onClick={() => navigate('/login')}
                          sx={{ color: '#999', fontSize: '13px', textTransform: 'none', padding: 0, minWidth: 'auto' }}
                        >
                          {t('Back to Login')}
                        </Button>
                      </Box>
                    </form>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          )}
          {showLearningJourney === false && (
            <Grid item xs={12} md={7.5} sx={{ display: { xs: 'none', md: 'block' } }}>
              <ImagePanel screen="emailVerify" />
            </Grid>
          )}
        </Grid>
      </Container>
      {showLearningJourney && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            overflowY: 'auto',
            zIndex: 1300,
            backgroundColor: 'rgba(255,255,255,0.95)',
          }}
        >
          <OnBoard onComplete={handleOnBoardComplete} comingfrom="login" comingJourney="normal"/>
        </Box>
      )}
      {onboardingLoading && (
        <Backdrop
          open
          sx={{
            zIndex: 1400,
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <CircularProgress color="primary" size={56} thickness={4} />
          <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 500 }}>
            Setting up your workspace...
          </Typography>
        </Backdrop>
      )}
      <SnackBar open={snackbar} snackbarTitle={snackbarTitle} close={() => setSnackbar(false)} />
    </Box>
  );
};
const useStyles = makeStyles((theme) => ({
  mainContainer: {
    background: '#F0F0F0',
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    overflowY: 'auto',
  },
  formcontainer: {
    background: '#FFFFFF',
    padding: '36px',
    borderRadius: '20px',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    [theme.breakpoints.down('sm')]: {
      padding: '24px',
    },
  },
  otpRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '8px',
    marginTop: '16px',
  },
  otpInput: {
    width: '100%',
    height: '52px',
    fontSize: '20px',
    textAlign: 'center',
    borderRadius: '12px',
    border: '1px solid #e0e0e0',
    boxShadow: 'rgba(99, 99, 99, 0.2) 0px 2px 8px 0px',
    outline: 'none',
    '&:focus': {
      border: '1px solid #00b673',
    },
  },
}));
